import { ProductId } from 'Utility/Product';
import DateHelper from 'Utility/DateHelper';
import { ISundayOfferData } from 'Utility/Helpers/IAP/ISundayOfferData';
import { IPayerReactivationOfferData } from "Utility/Helpers/IAP/IPayerReactivationOfferData";
import Point from 'Utility/Point';
import { IGameState } from 'State/Game/IGameState';
import { hasPurchasedReactivationOffer, IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { Immutable } from 'Utility/Types';
import { ISpecialOfferData } from 'Utility/Helpers/IAP/ISpecialOfferData';
import Game from 'Game';
import { addDragonbonesResources, areDBResourcesLoaded } from "Utility/Resource/ResourceHelper";
import { DRAGONBONES_PATH } from './Dragonbones';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { PopupOffersOfType, PopupOfferType } from 'Utility/Helpers/IAP/PopupOffers';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { IEventOfferData } from 'Utility/Helpers/IAP/IEventOfferData';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { getEventOfferProductId, queueEventOfferAssets } from 'State/Events/EventHelper';
import { cached } from 'Utility/async';

export const SHOP_UNLOCK_LEVEL: number = 4; // unlock immediately *after completing* level

export const STARTER_PACK_OFFER_MIN_PLAY_TIME: number = DateHelper.Day;
export const STARTER_PACK_OFFER_TIME_BETWEEN_SHOW: number = 3 * DateHelper.Day;

export const SPECIAL_OFFERS: Record<PopupOfferType, ISpecialOfferData> = {
    [PopupOfferType.EVENT_OFFER]: {
        priority: 100,
        condition: IAPStateHelper.isEventOfferAvailable,
        preload: queueEventOfferAssets,
        show: IAPStateHelper.showEventOfferPopup
    },
    [PopupOfferType.SUNDAY_OFFER]: {
        priority: 100,
        condition: IAPStateHelper.isSundayOfferAvailable,
        preload: () => {
            const { dragonbonesName: weeklyOfferDBName, layoutFileName } = IAPHelper.getWeeklySundayOfferData();
            addDragonbonesResources(weeklyOfferDBName, `${DRAGONBONES_PATH}/SundayOffer_v2/`);
            Game.resource.add('atlas-sunday-offer', 'resource/graphics/sprite_sheets/sunday_offer_v2.json');
            Game.resource.add('SundayOfferLayout', `resource/data/ui/${layoutFileName}.json`);
        },
        preloadCondition: IAPStateHelper.shouldLoadSundayOfferAssets,
        show: IAPStateHelper.showSundayOfferPopup
    },
    [PopupOfferType.PAYER_REACTIVATION_OFFER]: {
        priority: 10,
        condition: IAPStateHelper.isPayerReactivationOfferAvailable,
        preload: () => {
            Game.resource.add('payer_reactivation_offer', 'resource/graphics/sprite_sheets/payer_reactivation_offer.json');

            addDragonbonesResources('limited_offer_1', `${DRAGONBONES_PATH}/PayerReactivationOffer/`);
        },
        show: IAPStateHelper.showPayerReactivationPopup
    },
    [PopupOfferType.STARTER_OFFER]: {
        priority: 0,
        condition: IAPStateHelper.isStarterPackOfferAvailable,
        preload: () => {
        },
        show: IAPStateHelper.showStarterPackOfferPopup,
        forceShowIfAlreadySeen: true
    },
}

export const SUNDAY_OFFERS: Record<keyof PopupOffersOfType<PopupOfferType.SUNDAY_OFFER>, ISundayOfferData> = {
    [ProductId.SUNDAY_OFFER_V2_44_99]: {
        productId: ProductId.SUNDAY_OFFER_V2_44_99,
        dragonbonesName: 'sunday_offer_2',
        animationName: 'animtion0',
        armaturePosition: new Point(426, 580),
        layoutFileName: 'SundayOffer2Layout',
        buttonCacheKey: 'sunday_sale_2_icon',
    },
    [ProductId.SUNDAY_OFFER_V2]: {
        productId: ProductId.SUNDAY_OFFER_V2,
        dragonbonesName: 'limited_offer_2_export',
        armaturePosition: new Point(426, 580),
        layoutFileName: 'SundayOfferLayout',
        buttonCacheKey: 'sunday_sale_icon',
    }
}

export const PAYER_REACTIVATION_OFFERS: Record<keyof PopupOffersOfType<PopupOfferType.PAYER_REACTIVATION_OFFER>, IPayerReactivationOfferData> = {
    [ProductId.PAYER_REACTIVATION_OFFER]: {
        timeSinceLastPurchase: DateHelper.Day * 14,
        cooldownTime: DateHelper.Hour * 72,
        offerTime: DateHelper.Hour * 12,
        condition: (state: Immutable<IGameState>) => {
            return !hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER)
        }
    },
    [ProductId.PAYER_REACTIVATION_OFFER_2]: {
        timeSinceLastPurchase: DateHelper.Day * 7,
        cooldownTime: DateHelper.Hour * 72,
        offerTime: DateHelper.Hour * 12,
        condition: (state: Immutable<IGameState>) => {
            return (
                !hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER_2) &&
                hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER)
            )
        }
    },
    [ProductId.PAYER_REACTIVATION_OFFER_3]: {
        timeSinceLastPurchase: DateHelper.Day * 7,
        cooldownTime: DateHelper.Hour * 72,
        offerTime: DateHelper.Hour * 12,
        condition: (state: Immutable<IGameState>) => {
            return (
                !hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER_3) &&
                hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER) &&
                hasPurchasedReactivationOffer(state, ProductId.PAYER_REACTIVATION_OFFER_2)
            )
        }
    }
}

export const EVENT_OFFERS: Record<keyof PopupOffersOfType<PopupOfferType.EVENT_OFFER>, IEventOfferData> = {
    [EventType.XMAS_2021]: {
        condition: (state: IGameState): boolean => {
            const highestLevel = getHighestLevelCompleted(state.playerState);
            const productId = getEventOfferProductId(EventType.XMAS_2021);
            return highestLevel >= 22 && !IAPStateHelper.hasPurchasedProduct(state, productId);
        },
        queueAssets: () => {
            if (!areDBResourcesLoaded("christmas_offer")) {
                addDragonbonesResources('christmas_offer', `${DRAGONBONES_PATH}/ChristmasOffer/`);
            }
            if (!Game.resource.existsOrQueued("xmas_2021")) {
                Game.resource.add("xmas_2021", `resource/graphics/sprite_sheets/${EventType.XMAS_2021}.json`);
            }
        },
        buttonCacheKey: "christmas_map_icon"
    }
}

export const CACHED_SPECIAL_OFFER_DATA = cached<[PopupOfferType, ISpecialOfferData][]>(() => {
    return (Object.entries(SPECIAL_OFFERS) as unknown) as [PopupOfferType, ISpecialOfferData][];
});
